import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";
import { Clock, LogOut, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getPartnerAccess } from "@/lib/partner.functions";

export const Route = createFileRoute("/_authenticated/partner/pending")({
  head: () => ({ meta: [{ title: "Application Under Review — What Finland" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: PartnerPending,
});

function PartnerPending() {
  const navigate = useNavigate();
  const accessFn = useServerFn(getPartnerAccess);
  const { data: access, isLoading } = useQuery({
    queryKey: ["partner", "access"],
    queryFn: () => accessFn(),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (!access) return;
    if (access.hasClient) navigate({ to: "/partner" as never });
    else if (!access.signup || access.signup.status === "draft") navigate({ to: "/partner/onboarding" as never });
    else if (access.signup.status === "rejected") navigate({ to: "/partner/onboarding" as never });
  }, [access, navigate]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/partner/login" as never, replace: true });
  }

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white shadow-lg rounded-2xl p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
          <Clock className="h-6 w-6" />
        </div>
        <h1 className="mt-4 text-xl font-semibold text-slate-900">Application Under Review</h1>
        <p className="mt-2 text-sm text-slate-600">
          Thanks for applying to become a What Finland partner. Our team is reviewing your details and will be in touch shortly.
        </p>
        {access?.signup?.rejection_reason && (
          <p className="mt-4 text-xs text-rose-600 bg-rose-50 rounded-xl p-3">
            {access.signup.rejection_reason}
          </p>
        )}
        <button onClick={signOut} className="mt-6 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
          <LogOut className="h-3.5 w-3.5" /> Sign Out
        </button>
      </div>
    </div>
  );
}
